import { useEffect, useMemo, useRef } from 'react';
import { useSearchResultQuery } from '../queries/useSearchResultQuery';
import { useSearchResultMetaQuery } from '../queries/useSearchResultMetaQuery';
import { Flex, Spinner } from '@banhala/ui-kit';
import { useScrollToFilterList } from 'screens/src/components/hooks/useScrollToFilterList';
import { View, StyleSheet } from 'react-native';
import { AtomicRenderer, useFilterListAtom } from 'shared/src/component';
import { useLogViewEvent } from '../hook/useLogViewEvent';
import { useIsForegroundNow } from 'shared/src/hooks';
import type { ListRef } from '@banhala/ui-kit';
import type { IScreensComponent } from 'shared/src/api';

const styles = StyleSheet.create({
  spinnerContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -10 }, { translateY: -10 }],
    zIndex: 1000,
  },
});

type SearchResultListProps = {
  screenName: string;
  query: string;
  nextToken?: string;
  onScrollY?: (scrollY: number) => void;
  onScrollEndWorklet?: (scrollY: number) => void;
};

/**
 * 검색 결과 상품 리스트
 * - SearchResult 페이지 전용
 * - 검색어 기반 무한 스크롤 + 필터 연동
 */
export const SearchResultList = ({
  screenName,
  query,
  nextToken,
  onScrollY,
  onScrollEndWorklet,
}: SearchResultListProps) => {
  const listRef = useRef<ListRef<IScreensComponent> | null>(null);
  const isForeground = useIsForegroundNow();

  const {
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useSearchResultQuery({
    screenName,
    query,
    nextToken,
  });

  // iOS `MetaFeedReactor.fetchScreenAppreanceData` / Android `ComponentMetaViewModel`
  // 와 1:1 대칭. SEARCH_RESULT는 서버가 "3col"로 내려줘 3col 스켈레톤을 표시.
  const { data: skeletonType } = useSearchResultMetaQuery(screenName);

  // 지연 발화 대기 중 자동 prefetch — iOS의 willDisplay 기반 auto-loadmore와 동일한 의도
  // (VIEW 이벤트 유실 방지). prefetch 조건 자체는 select에서 계산됨.
  useEffect(() => {
    if (data?.shouldAutoPrefetchNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [data?.shouldAutoPrefetchNextPage, isFetchingNextPage, fetchNextPage]);

  // iOS: MetaFeedReactor.trackViewLoggingEvent → VIEW_{screenName}
  useLogViewEvent(data);

  // 필터/검색어 변경 시 AtomicRenderer의 displayHistoryRef 캐시 reset 트리거.
  // - 필터 적용: filterListAtomValue 객체 reference 변경
  // - 자동완성/연관검색어 inline 클릭(같은 인스턴스 내 query 변경): query 변경
  // - 재검색(close→open): 새 인스턴스로 자연 mount/unmount
  const { filterListAtomValue } = useFilterListAtom();
  const filterParams = useMemo(
    () => ({ ...filterListAtomValue, query }),
    [filterListAtomValue, query],
  );

  const itemHeightsRef = useRef<{ [key: number]: number }>({});
  const {
    filterListIndex,
    scrollToFilterList,
    onItemHeightChange,
    isPendingScroll,
  } = useScrollToFilterList({
    atomicRendererRef: listRef,
    components: data?.components,
    isFetching,
    itemHeightsRef,
  });

  if (isLoading) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      {isFetching && (
        <View style={styles.spinnerContainer}>
          <Spinner size={20} />
        </View>
      )}
      <AtomicRenderer
        refetch={refetch}
        data={data}
        fetchNextPage={fetchNextPage}
        listRef={listRef}
        filterListIndex={filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        isPendingScroll={isPendingScroll}
        filterParams={filterParams}
        itemHeightsRef={itemHeightsRef}
        onItemHeightChange={onItemHeightChange}
        onScrollY={onScrollY}
        skeletonType={skeletonType}
        onScrollEndWorklet={onScrollEndWorklet}
        isForeground={isForeground}
      />
    </>
  );
};
